"use client";

import cn from "@utils/cn";
import { useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { FC, useState } from "react";

import { Card, Editorial3ColumnsCardListProps } from "@/types";

import RichText from "../RichText/RichText";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "../ui/Button/Button";
import Icon from "../ui/Icon/Icon";
import Modal from "../ui/Modal/Modal";
import ModalContent from "../ui/Modal/ModalContent";

const EditorialThreeColumns: FC<Editorial3ColumnsCardListProps> = ({ entry }) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedTeamMember, setSelectedTeamMember] = useState<Card | null>(null);
  const t = useTranslations();

  const handleClick = (card: Card) => {
    setSelectedTeamMember(card);
    setModalOpen(true);
  };

  return (
    <>
      <div className="w-full">
        <div className="md:w-20\/24 mx-auto pb-m">
          {entry?.title && (
            <h2 className="uppercase text-center font-primary text-xl font-normal leading-9 tracking-widest pt-xxl pb-xl">{entry.title}</h2>
          )}
          <div className="w-full flex flex-row flex-wrap items-center justify-center">
            {entry?.cards?.map((card, index) => (
              <div
                key={index}
                className={cn("aspect-3-4 px-m md:px-xs my-xs w-full md:w-1/3", card.modal && "cursor-pointer")}
                onClick={() => handleClick(card)}
              >
                <div
                  className="rounded-lg shadow-md p-6 h-full flex flex-col items-center justify-end gap-xxs"
                  style={{ backgroundImage: `url(${card.image.url})`, backgroundPosition: "center", backgroundSize: "cover" }}
                >
                  <div className="img-overlay-2 py-l w-full">
                    <p className="text-white font-primary text-l leading-6 uppercase text-center font-medium px-m">{card.title}</p>
                    {card?.subtitle && <p className="text-white font-secondary text-m leading-6 text-center pt-m px-l">{card.subtitle}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedTeamMember && selectedTeamMember.modal && (
        <Modal open={modalOpen} onClose={() => setModalOpen(false)} className="flex items-center justify-center h-full w-full">
          <ModalContent className="!max-h-[100%] !rounded-none !px-0 w-full h-full md:w-[647px] md:h-[477px] bg-white md:!rounded-2xl p-4 overflow-hidden">
            <div className="py-l flex justify-between items-center sticky bg-white top-0 z-20">
              <p className="font-primary text-m font-medium uppercase px-l">{selectedTeamMember.modal.title}</p>
              <Button
                className="modal__close mx-l"
                size={ButtonSize.MEDIUM}
                variant={ButtonVariant.TERTIARY}
                theme={ButtonTheme.BLACK}
                onClick={() => setModalOpen(false)}
              >
                <span className="pointer-events-none">
                  <Icon name="close" size="24" title="close" className="stroke-black" />
                  <span className="sr-only">{t("button.label.closeModal")}</span>
                </span>
              </Button>
            </div>
            <div className="flex flex-col md:flex-row h-[calc(100dvh-96px)] md:h-full overflow-y-auto md:overflow-y-hidden px-l">
              <div className="w-full md:w-12\/24 md:h-full relative">
                <Image
                  src={selectedTeamMember.modal.image.url}
                  alt={selectedTeamMember.modal.image.title || selectedTeamMember.image.title || ""}
                  layout="fill"
                  objectFit="cover"
                  className="rounded-lg md:!h-[381px] !relative"
                />
              </div>
              <div className="w-full md:w-12\/24 pt-xxl md:pt-0 md:pl-l md:h-[381px] h-full md:overflow-y-auto">
                <h3 className="font-primary text-l font-medium uppercase">{selectedTeamMember.modal.name}</h3>
                <p className="font-primary text-s pt-m pb-xl uppercase font-normal">{selectedTeamMember.modal.role}</p>
                <p className="text-m font-secondary-light mb-xl leading-6 md:whitespace-break-spaces">
                  <RichText content={selectedTeamMember.modal.body} />
                </p>
                {selectedTeamMember.modal.cta && (
                  <Link
                    href={selectedTeamMember.modal.cta?.link?.url}
                    className="pb-xl md:pb-xs font-primary hover:text-gold text-s underline underline-offset-4 uppercase font-medium"
                  >
                    {selectedTeamMember.modal.cta.label}
                  </Link>
                )}
              </div>
            </div>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};
export default EditorialThreeColumns;
